/**
 * F43 — Visor de correo completo del Buzón.
 *
 * Cuerpo: GET /emails/:id/content parsea el .eml bajo demanda (HTML saneado
 * con imágenes cid reescritas + texto). El HTML se renderiza en un <iframe
 * sandbox> SIN allow-scripts ni allow-same-origin — esa es la defensa
 * principal contra correos maliciosos; los enlaces abren fuera vía
 * allow-popups. Si el parseo falla, cae al snippet (comportamiento antiguo).
 * Adjuntos: descarga individual vía /emails/:id/attachments/:attId.
 * Responder / Responder a todos / Reenviar abren el EmailComposer con hilo
 * real (In-Reply-To/References) y, en reenvíos, re-adjuntando los originales.
 */

import { useEffect, useState } from 'react'
import { currentLocaleTag } from '../../lib/formatting'
import {
  Paperclip,
  Download,
  Loader2,
  AlertCircle,
  Archive,
  ArchiveRestore,
  Mail,
  Reply,
  ReplyAll,
  Forward,
  ImageOff,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import type { EmailContent, EmailDetail } from '@jr2os/shared'
import ArchiveEmailModal from './ArchiveEmailModal'
import EmailComposer from '../comms/EmailComposer'

interface Props {
  emailId: string | null
  onArchived?: () => void
}

type ComposeMode = 'reply' | 'replyAll' | 'forward'

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** "Nombre <a@b.com>" → "a@b.com" (las direcciones IMAP llegan en ambos formatos). */
function extractEmail(s: string | null | undefined): string {
  if (!s) return ''
  const m = s.match(/<([^>]+)>/)
  return (m ? m[1]! : s).trim().toLowerCase()
}

/** Documento mínimo para el iframe: fondo claro, tipografía neutra, enlaces fuera. */
/**
 * ☠️ LAS IMÁGENES REMOTAS NO SE CARGAN SOLAS.
 *
 * Un `<img>` apuntando al servidor de quien escribe es un píxel de seguimiento:
 * al abrir el correo le dice a ese tercero que lo has leído, cuándo y desde qué
 * IP. Es exactamente lo que hace JR2 OS con sus campañas, así que sabemos de
 * primera mano que funciona.
 *
 * `img-src data:` deja pasar las imágenes que VIAJAN DENTRO del mensaje —las
 * `cid:` que el servidor ya ha incrustado como data-URI— y bloquea cualquier
 * descarga a un servidor de fuera. Es lo que hacen Gmail y Apple Mail, y por eso
 * hay un botón para levantarlo cuando hace falta ver el boletín entero.
 *
 * La CSP va como <meta> dentro del srcDoc porque el iframe tiene origen opaco
 * (sandbox sin allow-same-origin): no hay cabecera que se le pueda poner.
 */
function buildSrcDoc(html: string, mostrarImagenes: boolean): string {
  // ☠️ LISTA BLANCA, no parches. Con `default-src 'none'` de base, todo lo que
  // no se permita expresamente queda cortado: no solo las imágenes, también las
  // fuentes y los `@import` de un `<style>` —que el saneador conserva a
  // propósito— con los que un remitente puede llamar a casa igual que con un
  // píxel. Al levantar las imágenes se abre `img-src`/`media-src` y nada más.
  const csp =
    '<meta http-equiv="Content-Security-Policy" content="' +
    "default-src 'none'; " +
    `img-src data:${mostrarImagenes ? ' https:' : ''}; ` +
    `media-src data:${mostrarImagenes ? ' https:' : ''}; ` +
    "style-src 'unsafe-inline'; " +
    'font-src data:' +
    '">'
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    csp +
    '<base target="_blank">' +
    '<style>body{margin:12px;font-family:-apple-system,Segoe UI,sans-serif;font-size:14px;' +
    'color:#0f172a;background:#ffffff;word-break:break-word}img{max-width:100%;height:auto}</style>' +
    '</head><body>' +
    html +
    '</body></html>'
  )
}

/**
 * ¿El cuerpo trae algo de fuera que se vaya a bloquear? Si no, no se ofrece el
 * botón — ofrecerlo siempre sería ruido.
 *
 * ☠️ Mira TODO lo que la CSP corta, no solo `<img src>`: las plantillas de
 * boletín montan las cabeceras y los botones con `background="…"` y con
 * `background-image:url(…)` en el estilo, y `srcset` es habitual en las
 * modernas. Buscando solo `<img src=http`, el correo salía en blanco y sin
 * botón para arreglarlo.
 */
function tieneRecursosRemotos(html: string): boolean {
  return /(?:\bsrc|\bsrcset|\bbackground)\s*=\s*["']?(?:https?:)?\/\/|url\(\s*["']?(?:https?:)?\/\/|@import/i.test(
    html,
  )
}

export default function MessageReader({ emailId, onArchived }: Props) {
  const { t } = useTranslation(['email', 'common'])
  const [email, setEmail] = useState<EmailDetail | null>(null)
  const [content, setContent] = useState<EmailContent | null>(null)
  const [contentLoading, setContentLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [composeMode, setComposeMode] = useState<ComposeMode | null>(null)
  const [mostrarImagenes, setMostrarImagenes] = useState(false)

  useEffect(() => {
    // Permitir las imágenes es una decisión POR MENSAJE: al cambiar de correo
    // se vuelve a bloquear, o el permiso dado a un remitente acabaría valiendo
    // para el siguiente, que es justo lo que no se quiere.
    setMostrarImagenes(false)
    if (!emailId) {
      setEmail(null)
      setContent(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    setContent(null)
    api
      .get<{ email: EmailDetail }>(`/emails/${emailId}`)
      .then(({ email }) => {
        if (!cancelled) setEmail(email)
      })
      .catch((err: Error) => {
        if (cancelled) return
        if (err instanceof ApiError) {
          const body = err.body as { error?: string } | undefined
          setError(body?.error ?? err.message)
        } else {
          setError(err.message)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    // Cuerpo completo en paralelo; si falla, el visor cae al snippet.
    setContentLoading(true)
    api
      .get<EmailContent>(`/emails/${emailId}/content`)
      .then((c) => {
        if (!cancelled) setContent(c)
      })
      .catch(() => {
        if (!cancelled) setContent(null)
      })
      .finally(() => {
        if (!cancelled) setContentLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [emailId])

  const onUnarchive = async () => {
    if (!email) return
    if (!(await confirmDialog({ message: t('email:confirm.unarchive'), tone: 'danger' }))) return
    try {
      await api.delete(`/emails/${email.id}/archive`)
      onArchived?.()
      // re-fetch
      const { email: updated } = await api.get<{ email: EmailDetail }>(`/emails/${email.id}`)
      setEmail(updated)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common:error'))
    }
  }

  if (!emailId) {
    return (
      <div className="h-full grid place-items-center t-muted text-sm">
        <div className="text-center">
          <Mail size={28} className="mx-auto t-faint mb-2" />
          {t('email:emptyState')}
        </div>
      </div>
    )
  }

  if (loading && !email) {
    return (
      <div className="p-8 t-muted flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" /> {t('common:loading')}
      </div>
    )
  }

  if (error || !email) {
    return (
      <div className="p-8 text-rose-700 dark:text-rose-300 flex items-center gap-2">
        <AlertCircle size={16} />
        {error ?? t('email:notAvailable')}
      </div>
    )
  }

  const isArchived = email.archivedToClientId || email.archivedToOpportunityId
  const visibleAttachments = email.attachments.filter((a) => !a.inline)

  // ---------- Precarga del composer según el modo ----------
  const own = email.accountEmail.toLowerCase()
  // En un correo ENVIADO el remitente eres tú: responder «al remitente» abría
  // el composer con tu propia dirección en Para, y el cliente no recibía nada.
  // Lo que se continúa ahí es la conversación con QUIEN lo recibió.
  const esEnviado = email.direction === 'OUTBOUND'
  const senderEmail = esEnviado
    ? email.toAddresses.map((a) => extractEmail(a)).filter(Boolean).join(', ') ||
      email.fromEmail
    : extractEmail(email.replyToAddress) || email.fromEmail
  const quoteSource = content?.text ?? email.snippet
  const quoted = quoteSource
    .split('\n')
    .map((l) => `> ${l}`)
    .join('\n')
  const quoteHeader = t('email:quote.header', {
    date: new Date(email.receivedAt).toLocaleString(currentLocaleTag()),
    from: email.fromAddress,
  })
  const replyBody = `\n\n${quoteHeader}\n${quoted}\n`
  const forwardBody = `\n\n${t('email:quote.forwardHeader')}\n${t('email:from')} ${email.fromAddress}\n${t('email:to')} ${email.toAddresses.join(', ')}\n\n${quoteSource}\n`
  const baseSubject = email.subject ?? ''
  const replySubject = /^re:/i.test(baseSubject) ? baseSubject : `Re: ${baseSubject}`
  const forwardSubject = /^(fwd|rv|fw):/i.test(baseSubject) ? baseSubject : `Fwd: ${baseSubject}`
  const replyAllCc = Array.from(
    new Set(
      [...email.toAddresses, ...email.ccAddresses]
        .map(extractEmail)
        .filter((e) => e && e !== own && e !== senderEmail),
    ),
  ).join(', ')
  // Hilos larguísimos: raíz + 48 más recientes + este mensaje (el backend
  // también trunca; esto evita mandar payloads absurdos).
  const cappedRefs =
    content && content.references.length > 49
      ? [content.references[0]!, ...content.references.slice(-48)]
      : (content?.references ?? [])
  const threadRefs = content
    ? [...cappedRefs, ...(content.messageIdHeader ? [content.messageIdHeader] : [])]
    : []

  const actionBtnCls =
    'px-3 py-1.5 rounded-lg text-xs bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)] flex items-center gap-1.5'

  return (
    <div className="h-full overflow-y-auto">
      <header className="px-6 py-5 border-b border-base sticky top-0 backdrop-blur bg-topbar z-10">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold t-primary leading-tight">
              {email.subject || t('email:noSubject')}
            </h2>
            <div className="t-muted text-sm mt-1.5">
              <strong className="t-primary font-medium">{email.fromAddress}</strong>
              <span className="t-faint">
                {' '}
                · {new Date(email.receivedAt).toLocaleString(currentLocaleTag())}
              </span>
            </div>
            <div className="text-xs t-muted mt-1 space-y-0.5">
              {email.toAddresses.length > 0 && (
                <div>
                  <span className="t-faint">{t('email:to')} </span>
                  {email.toAddresses.join(', ')}
                </div>
              )}
              {email.ccAddresses.length > 0 && (
                <div>
                  <span className="t-faint">{t('email:cc')} </span>
                  {email.ccAddresses.join(', ')}
                </div>
              )}
            </div>
            {/* Acciones de respuesta */}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <button type="button" onClick={() => setComposeMode('reply')} className={actionBtnCls}>
                <Reply size={14} /> {t('email:reader.reply')}
              </button>
              {/* Solo si "a todos" añade algo respecto a "responder". */}
              {replyAllCc !== '' && (
                <button
                  type="button"
                  onClick={() => setComposeMode('replyAll')}
                  className={actionBtnCls}
                >
                  <ReplyAll size={14} /> {t('email:reader.replyAll')}
                </button>
              )}
              <button
                type="button"
                onClick={() => setComposeMode('forward')}
                className={actionBtnCls}
              >
                <Forward size={14} /> {t('email:reader.forward')}
              </button>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {isArchived ? (
              <>
                <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40">
                  {t('email:archivedIn')} {email.archivedToOpportunityCode ?? email.archivedToClientName ?? '—'}
                </span>
                <button type="button" onClick={onUnarchive} className={actionBtnCls}>
                  <ArchiveRestore size={14} /> {t('email:unarchive')}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setArchiveOpen(true)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5"
              >
                <Archive size={14} /> {t('email:archiveIn')}
              </button>
            )}
            {email.bodyUrl && (
              <a
                href={email.bodyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={actionBtnCls}
              >
                <Download size={14} /> {t('email:downloadEml')}
              </a>
            )}
          </div>
        </div>
      </header>

      <section className="px-6 py-5 space-y-4">
        {/* Cuerpo completo (F43): HTML en iframe sandbox / texto / fallback snippet */}
        {contentLoading && !content ? (
          <div className="t-muted text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> {t('email:reader.loadingBody')}
          </div>
        ) : content?.html ? (
          <>
            {!mostrarImagenes && tieneRecursosRemotos(content.html) && (
              <div className="flex items-center gap-2 text-xs rounded-lg border border-base bg-[var(--bg-input-soft)] px-3 py-2">
                <ImageOff size={14} className="t-faint shrink-0" />
                <span className="t-secondary flex-1">{t('email:reader.imagesBlocked')}</span>
                <button
                  type="button"
                  onClick={() => setMostrarImagenes(true)}
                  className="shrink-0 px-2 py-1 rounded t-primary hover:bg-hover font-medium"
                >
                  {t('email:reader.showImages')}
                </button>
              </div>
            )}
            <iframe
              title={email.subject ?? 'email'}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              referrerPolicy="no-referrer"
              srcDoc={buildSrcDoc(content.html, mostrarImagenes)}
              className="w-full rounded-lg border border-base bg-white"
              style={{ height: 'min(62vh, 720px)' }}
            />
          </>
        ) : content?.text ? (
          <p className="t-secondary text-sm leading-relaxed whitespace-pre-wrap break-words">
            {content.text}
          </p>
        ) : (
          <>
            <p className="t-secondary text-sm leading-relaxed whitespace-pre-wrap break-words">
              {email.snippet}
            </p>
            <p className="text-xs t-faint italic">{t('email:previewHint')}</p>
          </>
        )}

        {visibleAttachments.length > 0 && (
          <div>
            <h3 className="text-xs uppercase tracking-wider t-faint mb-2 flex items-center gap-1.5">
              <Paperclip size={12} /> {visibleAttachments.length}{' '}
              {visibleAttachments.length === 1 ? t('email:attachment') : t('email:attachments')}
            </h3>
            <ul className="space-y-1">
              {visibleAttachments.map((a) => (
                <li key={a.id} className="surface-soft px-3 py-2 text-sm flex items-center gap-3">
                  <Paperclip size={14} className="text-jr2-700 dark:text-jr2-300" />
                  <div className="flex-1 min-w-0">
                    <div className="t-primary truncate">{a.fileName}</div>
                    <div className="text-xs t-faint">
                      {a.mimeType} · {formatSize(a.sizeBytes)}
                    </div>
                  </div>
                  <a
                    href={`/api/emails/${email.id}/attachments/${a.id}`}
                    className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200"
                    title={t('email:reader.downloadAttachment')}
                  >
                    <Download size={14} />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {archiveOpen && (
        <ArchiveEmailModal
          emailId={email.id}
          subject={email.subject}
          onClose={() => setArchiveOpen(false)}
          onArchived={async () => {
            setArchiveOpen(false)
            onArchived?.()
            const { email: updated } = await api.get<{ email: EmailDetail }>(`/emails/${email.id}`)
            setEmail(updated)
          }}
        />
      )}

      {/* Composer contextual: responde desde la MISMA cuenta que recibió el correo. */}
      <EmailComposer
        open={composeMode !== null}
        onClose={() => setComposeMode(null)}
        accountId={email.accountId}
        defaultTo={composeMode === 'forward' ? '' : senderEmail}
        defaultCc={composeMode === 'replyAll' ? replyAllCc : ''}
        defaultSubject={composeMode === 'forward' ? forwardSubject : replySubject}
        defaultBody={composeMode === 'forward' ? forwardBody : replyBody}
        inReplyTo={
          composeMode !== 'forward' && content?.messageIdHeader
            ? content.messageIdHeader
            : undefined
        }
        references={composeMode !== 'forward' && threadRefs.length ? threadRefs : undefined}
        forwardOfEmailId={composeMode === 'forward' ? email.id : undefined}
      />
    </div>
  )
}
